/**
 * BrowserWorkRequestCard — read-only Hebrew/RTL display for a Browser Work
 * request (DIGEMP-BROWSER-WORK-FOUNDATION-ASAF-098). Shows the typed
 * request/readiness/evidence contract from
 * ../../../../backend/app/browser_work/domain.py as a human-readable card.
 *
 * This is a presentational leaf: it fetches nothing, calls no API and is
 * not wired into any route or page yet (095 wires it into the existing
 * ticket/conversation surfaces once DS locks clear). It renders whatever
 * `request` shape it is given, truthfully, including unknown/blocked
 * states and a missing/undefined request — it never assumes a friendlier
 * state than the data supports, and never invents a fallback "it probably
 * worked" reading of an unrecognized value.
 *
 * There is no button, link or control on this card that approves, executes,
 * retries or otherwise acts on the request. Approval and execution, if this
 * capability is ever built out, happen entirely outside this component.
 */

const UNKNOWN_META = {
  label: "לא ידוע",
  icon: "?",
  badge: "bg-gray-200 text-gray-600 border-gray-300",
  title: "ערך לא מוכר התקבל עבור שדה זה",
};

const READINESS_META = {
  unknown: {
    label: "מוכנות לא ידועה",
    icon: "?",
    badge: "bg-gray-200 text-gray-600 border-gray-300",
    title: "טרם נבדק אם הבקשה מוכנה",
  },
  blocked: {
    label: "חסום",
    icon: "⛔",
    badge: "bg-red-100 text-red-700 border-red-200",
    title: "הבקשה חסומה ואינה יכולה להתקדם",
  },
  not_ready: {
    label: "לא מוכן",
    icon: "…",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    title: "הבקשה עדיין לא עומדת בתנאים הנדרשים",
  },
  ready_for_approval: {
    label: "ממתין לאישור אנושי",
    icon: "⏳",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    title: "הבקשה עומדת בתנאים אך עדיין דורשת אישור אנושי מפורש",
  },
};

const APPROVAL_META = {
  not_requested: {
    label: "אישור לא התבקש",
    badge: "bg-gray-200 text-gray-600 border-gray-300",
  },
  pending: {
    label: "ממתין לאישור",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
  },
  approved: {
    label: "אושר",
    badge: "bg-green-100 text-green-700 border-green-200",
  },
  denied: {
    label: "נדחה",
    badge: "bg-red-100 text-red-700 border-red-200",
  },
  expired: {
    label: "פג תוקף",
    badge: "bg-gray-200 text-gray-500 border-gray-300",
  },
};

export function describeReadiness(state) {
  return READINESS_META[state] || { ...UNKNOWN_META, label: `מוכנות: ${state ?? "לא ידוע"}` };
}

export function describeApproval(state) {
  return APPROVAL_META[state] || { ...UNKNOWN_META, label: `אישור: ${state ?? "לא ידוע"}` };
}

function Badge({ meta }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badge}`}
      title={meta.title}
    >
      {meta.icon ? <span aria-hidden="true">{meta.icon}</span> : null}
      {meta.label}
    </span>
  );
}

function EvidenceList({ evidence }) {
  if (!evidence || evidence.length === 0) {
    return <p className="text-sm text-gray-500">אין ראיות מתועדות עבור בקשה זו.</p>;
  }
  return (
    <ul className="space-y-2">
      {evidence.map((item, index) => (
        <li
          key={index}
          className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
        >
          <span className="font-medium">{item?.kind || "לא ידוע"}: </span>
          <span>{item?.summary || "(ללא תקציר)"}</span>
        </li>
      ))}
    </ul>
  );
}

export default function BrowserWorkRequestCard({ request }) {
  if (!request) {
    return (
      <div dir="rtl" className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
        אין בקשת עבודת דפדפן להצגה.
      </div>
    );
  }

  const isBrowserChannel = request.channel === "browser";
  const readinessMeta = describeReadiness(request.readiness);
  const approvalMeta = describeApproval(request.approval);

  return (
    <div dir="rtl" className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" data-testid="browser-work-request-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">בקשת עבודת דפדפן</h3>
        <div className="flex items-center gap-2">
          <Badge meta={readinessMeta} />
          <Badge meta={approvalMeta} />
        </div>
      </div>

      {!isBrowserChannel ? (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          ערוץ הבקשה ({request.channel ?? "לא ידוע"}) אינו נתמך על ידי רכיב זה — מוצג כאן רק מידע
          עבור בקשות בערוץ דפדפן מפורש.
        </p>
      ) : null}

      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-gray-500">עובד דיגיטלי</dt>
          <dd className="text-gray-900">{request.employee_name || request.employee_id || "לא ידוע"}</dd>
        </div>
        <div>
          <dt className="text-gray-500">קריאה</dt>
          <dd className="text-gray-900">
            {request.ticket_number ? `#${request.ticket_number}` : request.ticket_id || "לא ידוע"}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">לקוח</dt>
          <dd className="text-gray-900">{request.customer_name || request.customer_id || "לא ידוע"}</dd>
        </div>
        <div>
          <dt className="text-gray-500">מערכת יעד</dt>
          <dd className="text-gray-900">{request.target_system || "לא ידוע"}</dd>
        </div>
      </dl>

      <div>
        <dt className="text-sm text-gray-500">מטרת הבקשה</dt>
        <dd className="mt-0.5 whitespace-pre-wrap text-sm text-gray-900">
          {request.objective || "לא סופקה מטרה"}
        </dd>
      </div>

      {request.status_reason ? (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {request.status_reason}
        </p>
      ) : null}

      <div>
        <h4 className="text-sm font-medium text-gray-700">ראיות</h4>
        <div className="mt-1">
          <EvidenceList evidence={request.evidence} />
        </div>
      </div>

      <p className="border-t border-gray-100 pt-2 text-xs text-gray-400">
        רכיב זה מציג מידע בלבד ואינו מבצע ואינו מאשר אף פעולה. אישור וביצוע, אם וכאשר ייבנו,
        מתבצעים אך ורק מחוץ לרכיב זה ובכפוף למדיניות האישורים.
      </p>
    </div>
  );
}
